import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import { toWords } from 'number-to-words';
import Button from 'chameleon/ui-stack/ui/base/button';
import ConfirmDialog from 'chameleon/ui-stack/ui/modals/confirm-dialog';
import { LoadingSpinner, observable } from 'legacy-packages/ft-cb';
import SubmitSuccessContent from '../SubmitSuccessContent';

import './styles.scss';

const LEARNOSITY_UNANSWERED_FLAG = 'not_attempted';

/**
 * Check if a question is answered.
 * A question is considered answered if at least one answer is given.
 * */
const isAnsweredQuestion = (answersData) =>
  answersData.some((answerData) => {
    const { type, value, wordCount, length } = answerData;

    if (type === 'string') {
      return wordCount > 0 || /<img([\w\W]+?)\/?>/.test(value.trim());
    }

    if (type === 'array') {
      return value.length > 0;
    }

    if (type === 'audio+png') {
      return length > 0;
    }

    return true;
  });

export const UnAnsweredQuestion = ({ title, onClick }) => (
  <Button isLink isNaked onClick={onClick}>
    {title}
  </Button>
);

const UnAnsweredQuestions = ({
  questions,
  onNavigateToQuestion,
  getQuestionPosition,
}) => {
  const questionText =
    questions.length > 1 ? ' questions are ' : ' question is ';
  let countWord = toWords(questions.length);
  countWord = countWord.charAt(0).toUpperCase() + countWord.slice(1);
  return (
    <div className="flex flex-col gap-2">
      <div className="font-medium leading-normal">
        {`${countWord} ${questionText} unanswered.`}
      </div>
      <ul className="flex flex-col gap-2 desktop:max-h-40 overflow-auto SubmitConfirmationPopover--questions-list">
        {/* learnosity returns zero based see getQuestionPosition in Parent Component */}
        {questions.map((q) => (
          <li key={q}>
            <UnAnsweredQuestion
              onClick={() => onNavigateToQuestion(q)}
              title={`Question ${getQuestionPosition(q) + 1}`}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};

class SubmitConfirmationPopover extends PureComponent {
  static displayName = 'SubmitConfirmationPopover';

  static propTypes = {
    app: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired,
    isSoftTime: PropTypes.bool.isRequired,
    onExitPlayer: PropTypes.func,
    learnositySubmit: PropTypes.func.isRequired,
    isImpersonating: PropTypes.bool.isRequired,
    areMcqQuestions: PropTypes.bool,
  };

  static defaultProps = {
    onExitPlayer: () => {},
    followPracticeFlow: false,
    areMcqQuestions: false,
  };

  constructor(props) {
    super(props);

    this.state = {
      isSubmitted: false,
      unansweredQuestions: null,
      isExitButtonDisabled: false,
    };
  }

  componentDidMount() {
    const { app } = this.props;
    const items = app.getItems();

    const totalQuestionsLength = Object.keys(items).reduce(
      (acc, key) => acc + items[key].questions.length,
      0,
    );

    const loadedQuestionsLength = Object.keys(app.getQuestions()).length;

    if (loadedQuestionsLength !== totalQuestionsLength) {
      app.on('items:lazyload:complete', this.checkUnansweredQuestions);
    } else {
      this.checkUnansweredQuestions();
    }
    observable.subscribe('submissionReady', this.submissionReadyCallback);
  }

  componentWillUnmount() {
    const { app } = this.props;
    app.off('items:lazyload:complete', this.checkUnansweredQuestions);
    observable.unsubscribe('submissionReady', this.submissionReadyCallback);
  }

  submissionReadyCallback = (submissionReady) => {
    this.setState({
      isExitButtonDisabled: !submissionReady,
    });
  };

  onNavigateToQuestion = (questionRef) => {
    //close the Confirmation popover and navigate
    this.props.onClose();
    const itemApp = this.props.app.items();
    itemApp.goto(questionRef);
  };

  /**
   * Obtains the position (zero based index) of a given item.
   * see https://docs.learnosity.com/assessment/items/methods#assessApp-GetItemPosition.itemReference
   */
  getQuestionPosition = (questionRef) => {
    const { app } = this.props;
    return app.assessApp().getItemPosition(questionRef);
  };

  checkUnansweredQuestions = () => {
    const { app } = this.props;
    const items = app.getItems();
    const itemResponses = app.assessApp().getItemResponses();

    const references = Object.keys(items);
    let unansweredQuestions = references.filter(
      (ref) => items[ref].attempt_status === LEARNOSITY_UNANSWERED_FLAG,
    );
    let potentialUnansweredQuestions = references.filter(
      (ref) => items[ref].attempt_status !== LEARNOSITY_UNANSWERED_FLAG,
    );

    //Find those questions which value is "" , which means that the student had started to answer
    //and then erased the whole answer.
    potentialUnansweredQuestions = potentialUnansweredQuestions.filter(
      (ref) => !isAnsweredQuestion(Object.values(itemResponses[ref])),
    );
    unansweredQuestions = unansweredQuestions.concat(
      potentialUnansweredQuestions,
    );
    unansweredQuestions = references.filter((ref) =>
      unansweredQuestions.includes(ref),
    );
    this.setState({
      unansweredQuestions,
    });
  };

  handleSubmit = () => {
    if (this.props.followPracticeFlow) {
      return this.props.onShowModal('PracticeConfirmation');
    }
    this.setState({ isSubmitted: true });
    this.props.learnositySubmit();
  };

  render() {
    const {
      onClose,
      isSoftTime,
      onExitPlayer,
      isImpersonating,
      areMcqQuestions,
    } = this.props;
    const { isSubmitted, unansweredQuestions, isExitButtonDisabled } =
      this.state;
    const isLoading = unansweredQuestions === null;
    const questionLength = unansweredQuestions && unansweredQuestions.length;
    const footerInfo = isSoftTime
      ? 'Are you sure you want to submit your assessment?'
      : 'Are you sure you want to submit your exam?';

    let modalContent;
    let isPrimaryBtnDisabled;
    if (isSubmitted) {
      isPrimaryBtnDisabled =
        isLoading && isExitButtonDisabled && !isImpersonating;
      modalContent = {
        btnPrimary: 'Exit',
        btnPrimaryAria: {
          'data-test-button-exit': true,
        },
      };
    } else {
      isPrimaryBtnDisabled = isLoading;
      modalContent = {
        btnPrimary: 'Yes',
        btnSecondary: 'No',
      };
    }

    return (
      <ConfirmDialog
        size="tiny"
        title="Submit"
        onSuccess={isSubmitted ? onExitPlayer : this.handleSubmit}
        onClose={isSubmitted ? onExitPlayer : onClose}
        content={modalContent}
        isPrimaryBtnDisabled={isPrimaryBtnDisabled}
        aria={{
          'aria-live': 'polite',
        }}
        dialogClassName="SubmitConfirmationPopover"
      >
        {isLoading ? (
          <LoadingSpinner data-test-learnosity-loading />
        ) : isSubmitted ? (
          <SubmitSuccessContent
            isSoftTime={isSoftTime}
            areMcqQuestions={areMcqQuestions}
          />
        ) : (
          <div className="flex flex-col gap-8">
            {questionLength > 0 && (
              <UnAnsweredQuestions
                questions={unansweredQuestions}
                onNavigateToQuestion={this.onNavigateToQuestion}
                getQuestionPosition={this.getQuestionPosition}
              />
            )}
            <p className="m-0">{footerInfo}</p>
          </div>
        )}
      </ConfirmDialog>
    );
  }
}

export default SubmitConfirmationPopover;
